import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Briefcase,
  ArrowLeftRight,
  Settings,
  BarChart3,
  Activity,
  FileText,
  ScrollText,
  Bot,
  CreditCard,
  ChevronLeft,
  Shield,
  CandlestickChart,
} from 'lucide-react';
import { cn } from '@/utils';
import { useAppStore } from '@/stores/appStore';

const navigation = [
  { name: 'Overview', href: '/', icon: LayoutDashboard },
  { name: 'Market', href: '/market', icon: CandlestickChart },
  { name: 'Portfolio', href: '/portfolio', icon: Briefcase },
  { name: 'Trades', href: '/trades', icon: ArrowLeftRight },
  { name: 'Risk', href: '/risk', icon: Shield },
  { name: 'Accounts', href: '/accounts', icon: CreditCard },
  { name: 'Analytics', href: '/analytics', icon: BarChart3 },
  { name: 'AI Control', href: '/ai', icon: Bot },
  { name: 'Reports', href: '/reports', icon: FileText },
  { name: 'Settings', href: '/settings', icon: Settings },
  { name: 'System Health', href: '/health', icon: Activity },
  { name: 'Logs', href: '/logs', icon: ScrollText },
];

export function Sidebar() {
  const location = useLocation();
  const { sidebarOpen, toggleSidebar } = useAppStore();

  return (
    <aside
      className={cn(
        'fixed left-0 top-0 z-40 h-screen bg-surface-900 border-r border-surface-800 transition-all duration-300',
        sidebarOpen ? 'w-64' : 'w-20',
      )}
    >
      {/* Logo */}
      <div className="flex items-center justify-between h-16 px-4 border-b border-surface-800">
        <Link to="/" className="flex items-center gap-3">
          <div className="w-9 h-9 bg-brand-600 rounded-lg flex items-center justify-center flex-shrink-0">
            <Shield className="w-5 h-5 text-white" />
          </div>
          {sidebarOpen && (
            <span className="text-lg font-bold text-surface-100 whitespace-nowrap">
              AI Crypto
            </span>
          )}
        </Link>
        <button
          onClick={toggleSidebar}
          className={cn(
            'p-1.5 rounded-lg text-surface-500 hover:text-surface-300 hover:bg-surface-800 transition-all',
            !sidebarOpen && 'rotate-180',
          )}
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex flex-col gap-1 p-3 mt-2">
        {navigation.map((item) => {
          const isActive =
            location.pathname === item.href ||
            (item.href !== '/' && location.pathname.startsWith(item.href));
          const Icon = item.icon;

          return (
            <Link
              key={item.name}
              to={item.href}
              className={cn(
                isActive ? 'sidebar-link-active' : 'sidebar-link',
                !sidebarOpen && 'justify-center px-2',
              )}
              title={!sidebarOpen ? item.name : undefined}
            >
              <Icon className="w-5 h-5 flex-shrink-0" />
              {sidebarOpen && <span>{item.name}</span>}
            </Link>
          );
        })}
      </nav>

      {/* Status indicator */}
      {sidebarOpen && (
        <div className="absolute bottom-4 left-0 right-0 px-4">
          <div className="card p-3">
            <div className="flex items-center gap-2 text-xs text-surface-400">
              <div className="w-2 h-2 rounded-full bg-profit animate-pulse-slow" />
              Paper Mode Active
            </div>
            <p className="text-[10px] text-surface-600 mt-1">
              Live trading requires activation
            </p>
          </div>
        </div>
      )}
    </aside>
  );
}
